import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { assistanceAPI, peoplePlatformAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from './Toast';
import ModalShell from './ModalShell';
import MmSelect from './MmSelect';
import { searchFieldsMatch } from '../utils/searchText';

const EVENT_CATEGORY_KEYS = [
  'deces',
  'hospitalisation',
  'maladie',
  'naissance',
  'mariage',
  'catastrophe',
  'autre',
];

function memberLabel(m) {
  const last = String(m?.last_name || '').trim();
  const first = String(m?.first_name || '').trim();
  if (last && first) return `${last}, ${first}`;
  return String(m?.name || '').trim() || String(m?.id || '');
}

function toFriendlyAssistanceCreateError(err, t) {
  const detail = String(err?.response?.data?.detail || '').trim();
  const lower = detail.toLowerCase();
  const p = 'assistanceManagement.newCaseModal.errors';
  if (!detail) return t(`${p}.createImpossible`);

  if (lower.includes('event_category is not covered')) {
    return t(`${p}.eventNotCovered`);
  }
  if (lower.includes('requested_amount is below mechanism min_amount')) {
    return t(`${p}.belowMechMin`);
  }
  if (lower.includes('requested_amount exceeds mechanism max_amount')) {
    return t(`${p}.exceedsMechMax`);
  }
  if (lower.includes('requested_amount is below category min_amount')) {
    return t(`${p}.belowCatMin`);
  }
  if (lower.includes('requested_amount exceeds category max_amount')) {
    return t(`${p}.exceedsCatMax`);
  }
  if (lower.includes('requested_amount is below configured min_amount')) {
    return t(`${p}.belowCfgMin`);
  }
  if (lower.includes('requested_amount exceeds configured max_amount')) {
    return t(`${p}.exceedsCfgMax`);
  }
  if (lower.includes('member does not meet minimum seniority')) {
    return t(`${p}.seniorityMin`);
  }
  if (lower.includes('member seniority is insufficient')) {
    return t(`${p}.seniorityInsufficient`);
  }
  if (lower.includes('member contribution situation is not compliant')) {
    return t(`${p}.contributionNotCompliant`);
  }
  if (lower.includes('member contribution situation is not up to date')) {
    return t(`${p}.contributionNotUpToDate`);
  }
  if (lower.includes('reason is required by assistance workflow')) {
    return t(`${p}.reasonRequired`);
  }
  if (lower.includes('mechanism is not active')) {
    return t(`${p}.mechanismInactive`);
  }
  if (lower.includes('solidarity mechanism not found')) {
    return t(`${p}.mechanismNotFound`);
  }
  if (lower.includes('project context required')) {
    return t(`${p}.projectContextRequired`);
  }
  return detail;
}

/**
 * Création d’une demande d’allocation : mécanisme, membre (recherche), catégorie, motif.
 * Le montant de référence provient des paramètres projet (`assistance.default_case_amount`).
 */
const AssistanceNewCaseModal = ({
  open,
  onClose,
  mechanisms,
  fixedRequestedAmount,
  canCreate,
  preselectedMemberId,
  lockMemberSelection = false,
  onCreated,
  /** Mode édition (membre) : brouillon ou en instruction, pas encore approuvé */
  editingCase = null,
}) => {
  const { t } = useTranslation('common');
  const { success, error } = useToast();
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [mechanismId, setMechanismId] = useState('');
  const [memberProfileId, setMemberProfileId] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [reason, setReason] = useState('');
  const [editAmountText, setEditAmountText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nk = 'assistanceManagement.newCaseModal';

  useEffect(() => {
    if (!open || !canCreate) return;
    if (lockMemberSelection) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingMembers(true);
        const res = await peoplePlatformAPI.list({ limit: 2000, skip: 0 });
        if (!cancelled) setMembers(mapPeopleListResponse(res.data));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMembers([]);
          error(e?.response?.data?.detail || t(`${nk}.loadMembersError`));
        }
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canCreate, lockMemberSelection, error, t]);

  useEffect(() => {
    if (!open) return;
    if (editingCase) {
      setMemberQuery('');
      setMechanismId(String(editingCase.mechanism_id || '').trim());
      setMemberProfileId(String(editingCase.member_profile_id || preselectedMemberId || '').trim());
      setEventCategory(String(editingCase.event_category || '').trim().toLowerCase());
      setReason(String(editingCase.reason || ''));
      setEditAmountText(
        editingCase.requested_amount != null && editingCase.requested_amount !== ''
          ? String(editingCase.requested_amount)
          : '',
      );
    } else {
      setMemberQuery('');
      setMechanismId('');
      setMemberProfileId(String(preselectedMemberId || '').trim());
      setEventCategory('');
      setReason('');
      setEditAmountText('');
    }
  }, [open, preselectedMemberId, editingCase]);

  const filteredMembers = useMemo(() => {
    if (!String(memberQuery || '').trim()) return members.slice(0, 80);
    return members
      .filter((m) =>
        searchFieldsMatch(memberQuery, [memberLabel(m), m.id, m.email, m.phone])
      )
      .slice(0, 120);
  }, [members, memberQuery]);

  const selectedMember = useMemo(
    () => members.find((m) => String(m.id) === String(memberProfileId)),
    [members, memberProfileId]
  );

  const amountNum = editingCase
    ? Number(String(editAmountText || '').replace(/\s/g, '').replace(',', '.'))
    : Number(fixedRequestedAmount);
  const amountOk = Number.isFinite(amountNum) && amountNum > 0;
  const isDraftEdit = !editingCase || String(editingCase.status || '').toLowerCase() === 'draft';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    if (!String(mechanismId).trim() || !String(memberProfileId).trim() || !String(eventCategory).trim() || !String(reason).trim()) {
      error(t(`${nk}.validateFill`));
      return;
    }
    if (!amountOk) {
      error(
        editingCase ? t(`${nk}.validateAmountEdit`) : t(`${nk}.validateAmountSettings`),
      );
      return;
    }
    try {
      setSubmitting(true);
      if (editingCase?.id) {
        const patch = {
          event_category: String(eventCategory).trim().toLowerCase(),
          reason: String(reason).trim(),
          requested_amount: amountNum,
        };
        if (isDraftEdit) {
          patch.mechanism_id = String(mechanismId).trim();
        }
        await assistanceAPI.updateCase(String(editingCase.id), patch);
        success(t(`${nk}.successUpdated`));
      } else {
        await assistanceAPI.createCase({
          mechanism_id: String(mechanismId).trim(),
          member_profile_id: String(memberProfileId).trim(),
          event_category: String(eventCategory).trim().toLowerCase(),
          reason: String(reason).trim(),
          requested_amount: amountNum,
        });
        success(t(`${nk}.successCreated`));
      }
      onCreated?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      error(toFriendlyAssistanceCreateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <ModalShell
      maxWidthClass="max-w-lg"
      onBackdropClick={() => !submitting && onClose?.()}
      panelClassName="flex min-h-0 flex-col !overflow-hidden p-0"
    >
      <div className="mm-modal-header">
        <div className="min-w-0">
          <p className="mm-hero__label text-[#1f6ab8]">
            {t('memberPortal.allocation')}
          </p>
          <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
            {editingCase ? t(`${nk}.titleEdit`) : t(`${nk}.titleCreate`)}
          </h2>
          <p className="mt-1 text-xs leading-snug text-slate-600">
            {editingCase ? (
              t(`${nk}.subtitleEdit`)
            ) : amountOk ? (
              t(`${nk}.subtitleReferenceOk`)
            ) : (
              <>
                {t(`${nk}.subtitleAmountMissingBeforeLink`)}{' '}
                <Link
                  to="/settings?tab=assistance"
                  className="font-semibold text-[#113a5b] underline-offset-2 hover:underline"
                >
                  {t(`${nk}.settingsLink`)}
                </Link>
                {t(`${nk}.subtitleAmountMissingAfter`)}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => !submitting && onClose?.()}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
          aria-label={t(`${nk}.closeAria`)}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
          <div className="mm-card space-y-3 p-3 sm:p-3.5">
            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelMechanism`)}
              </label>
              <MmSelect
                value={mechanismId}
                onChange={setMechanismId}
                disabled={submitting || (Boolean(editingCase) && !isDraftEdit)}
                required
                placeholder={t(`${nk}.mechanismPlaceholder`)}
                options={mechanisms.map((m) => ({
                  value: m.id,
                  label: m.name || m.id,
                }))}
              />
            </div>

            {lockMemberSelection ? (
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {t(`${nk}.labelBeneficiary`)}
                </label>
                <div className="rounded-xl border border-[#bcd4f6] bg-gradient-to-r from-[#edf4ff] to-white px-3 py-2.5 text-sm font-medium text-[#0f3350]">
                  {t(`${nk}.lockMemberHint`)}
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {t(`${nk}.labelBeneficiary`)}
                </label>
                <input
                  type="search"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder={t(`${nk}.searchMembersPlaceholder`)}
                  disabled={submitting || loadingMembers}
                  className="mm-field mb-2"
                />
                {selectedMember ? (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-[#bcd4f6] bg-gradient-to-r from-[#edf4ff] to-white px-3 py-2.5 text-sm">
                    <span className="font-bold text-[#0f3350]">{memberLabel(selectedMember)}</span>
                    <span className="text-xs text-slate-600">
                      {selectedMember.email || selectedMember.phone || selectedMember.id}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-xs font-bold text-[#113a5b]"
                      onClick={() => setMemberProfileId('')}
                    >
                      {t(`${nk}.changeMember`)}
                    </button>
                  </div>
                ) : null}
                <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-200/90 bg-white/70">
                  {loadingMembers ? (
                    <p className="p-4 text-center text-sm text-slate-500">{t(`${nk}.loadingMembers`)}</p>
                  ) : filteredMembers.length === 0 ? (
                    <p className="p-4 text-center text-sm text-slate-500">{t(`${nk}.emptyMemberSearch`)}</p>
                  ) : (
                    <ul className="divide-y divide-slate-100/90">
                      {filteredMembers.map((m) => (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => setMemberProfileId(String(m.id))}
                            className={`mm-press flex w-full flex-col items-start px-3 py-2.5 text-left text-sm ${
                              String(memberProfileId) === String(m.id)
                                ? 'bg-[#edf4ff] ring-1 ring-inset ring-[#2f6fd4]/35'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <span className="font-bold text-[#0f3350]">{memberLabel(m)}</span>
                            <span className="text-xs text-slate-600">
                              {[m.email, m.phone].filter(Boolean).join(' · ') || m.id}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mm-card space-y-3 p-3 sm:p-3.5">
            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelEventCategory`)}
              </label>
              <MmSelect
                value={eventCategory}
                onChange={setEventCategory}
                disabled={submitting}
                required
                placeholder={t(`${nk}.eventCategoryPlaceholder`)}
                options={EVENT_CATEGORY_KEYS.map((key) => ({
                  value: key,
                  label: t(`memberDashboard.eventCategory.${key}`),
                }))}
              />
              <p className="mt-1.5 text-xs text-slate-500">{t(`${nk}.eventCategoriesNote`)}</p>
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelReason`)}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                required
                disabled={submitting}
                placeholder={t(`${nk}.reasonPlaceholder`)}
                className="mm-field"
              />
            </div>

            {editingCase ? (
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {t(`${nk}.labelRequestedAmount`)}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={editAmountText}
                  onChange={(e) => setEditAmountText(e.target.value)}
                  disabled={submitting}
                  required
                  className="mm-field"
                />
                <p className="mt-1.5 text-xs text-slate-500">{t(`${nk}.amountThresholdHint`)}</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mm-modal-footer shrink-0">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            className="mm-cta mm-cta--ghost flex-1"
          >
            {t('assistanceManagement.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !canCreate || !amountOk}
            className="mm-cta flex-1 disabled:opacity-50"
          >
            {submitting
              ? editingCase
                ? t(`${nk}.savingEdit`)
                : t(`${nk}.savingCreate`)
              : editingCase
                ? t(`${nk}.submitEdit`)
                : t(`${nk}.submitCreate`)}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

export default AssistanceNewCaseModal;
